import type { OfflineQueue } from "./offline-queue.js";

export interface QueuedEvent {
  type: string;
  [key: string]: unknown;
}

export interface BatchQueueConfig {
  flushAt: number;
  flushInterval: number;
  onFlush: (batch: QueuedEvent[]) => Promise<void>;
  onBeaconFlush?: (batch: QueuedEvent[]) => boolean;
  offlineQueue?: OfflineQueue;
}

export class BatchQueue {
  private queue: QueuedEvent[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly config: BatchQueueConfig;
  private readonly onVisibilityChange: () => void;
  private readonly onBeforeUnload: () => void;
  private _queuedOfflineCount = 0;

  get queuedOfflineCount(): number {
    return this._queuedOfflineCount;
  }

  constructor(config: BatchQueueConfig) {
    this.config = config;

    this.onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        if (this.queue.length === 0) return;
        if (this.config.onBeaconFlush) {
          const snapshot = this.queue.splice(0);
          this.config.onBeaconFlush(snapshot);
        } else {
          void this._flushBackground();
        }
      }
    };

    this.onBeforeUnload = () => {
      if (this.queue.length === 0) return;
      if (this.config.onBeaconFlush) {
        const snapshot = this.queue.splice(0);
        this.config.onBeaconFlush(snapshot);
      } else {
        void this._flushBackground();
      }
    };
  }

  enqueue(event: QueuedEvent): void {
    this.queue.push(event);

    if (this.queue.length >= this.config.flushAt) {
      void this._flushBackground();
    }
  }

  /** Fire-and-forget flush — swallows errors after offline handling */
  private _flushBackground(): Promise<void> {
    return this.flush().catch(() => undefined);
  }

  async flush(): Promise<void> {
    if (this.queue.length === 0) {
      // drain offline queue if present (re-enqueue rescued events)
      if (this.config.offlineQueue) {
        const drained = await this.config.offlineQueue.drain();
        if (drained.length > 0) {
          this.queue.push(...drained);
        } else {
          return;
        }
      } else {
        return;
      }
    }

    const snapshot = this.queue.splice(0);

    const sorted = snapshot.sort((a, b) => {
      const aIsPage = a.type === "page" ? 0 : 1;
      const bIsPage = b.type === "page" ? 0 : 1;
      return aIsPage - bIsPage;
    });

    try {
      await this.config.onFlush(sorted);
      // Successful flush — drain offline queue for next round
      if (this.config.offlineQueue) {
        const drained = await this.config.offlineQueue.drain();
        if (drained.length > 0) this.queue.push(...drained);
      }
    } catch (err) {
      if (
        this.config.offlineQueue &&
        typeof navigator !== "undefined" &&
        navigator.onLine === false
      ) {
        await this.config.offlineQueue.save(sorted);
        this._queuedOfflineCount += sorted.length;
      }
      throw err;
    }
  }

  start(): void {
    this.timer = setInterval(() => {
      if (this.queue.length > 0) {
        void this._flushBackground();
      }
    }, this.config.flushInterval);

    document.addEventListener("visibilitychange", this.onVisibilityChange);
    window.addEventListener("beforeunload", this.onBeforeUnload);
  }

  stop(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }

    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    window.removeEventListener("beforeunload", this.onBeforeUnload);
  }

  get pending(): number {
    return this.queue.length;
  }

  /** @internal — read-only view of the current queue for testing */
  get _items(): readonly QueuedEvent[] {
    return this.queue;
  }
}
